import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, post } from './api';
import { useAuth } from './auth';
import { can } from './permissions';
export type StationUpdate = { id: string; capable: boolean; available: boolean; job: null | {
  id: string; state: 'REQUESTED' | 'DOWNLOADING' | 'REBOOTING' | 'SUCCEEDED' | 'CANCELLED'; version: string; received: number; size: number; error: string; updated_at: string;
} };
export type StationFirmwareCatalog = { release: null | { id: string; version: string; notes: string[] }; stations: StationUpdate[] };
export function StationFirmwareNotice() {
  const { user } = useAuth(), location = useLocation();
  const allowed = can(user, 'printing.manage');
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!allowed) { setCount(0); return; }
    let active = true, loading = false;
    const load = async () => {
      if (loading || document.visibilityState === 'hidden') return;
      loading = true;
      try { const data = await api<StationFirmwareCatalog>('/printing/firmware'); if (active) setCount(data.stations.filter(s => s.available).length); }
      catch { if (active) setCount(0); }
      finally { loading = false; }
    };
    void load(); const timer = setInterval(() => void load(), 60000);
    document.addEventListener('visibilitychange', load);
    return () => { active = false; clearInterval(timer); document.removeEventListener('visibilitychange', load); };
  }, [allowed]);
  return allowed && count > 0 && location.pathname !== '/connection-stations' ? <p className="stations-message success" role="status">Có phần mềm mới cho {count} trạm. <Link to="/connection-stations">Xem và cập nhật</Link></p> : null;
}
export function stationUpdateLabel(job: NonNullable<StationUpdate['job']>) {
  switch (job.state) {
    case 'REQUESTED': return 'Đang chờ trạm nhận cập nhật';
    case 'DOWNLOADING': return job.error ? 'Tải bị gián đoạn — trạm sẽ tự thử lại' : 'Trạm đang tải và cài bản cập nhật';
    case 'REBOOTING': return 'Đang khởi động lại và kiểm tra phiên bản mới';
    case 'SUCCEEDED': return `Đã cập nhật thành công · ${job.version}`;
    default: return 'Đã hủy yêu cầu cập nhật';
  }
}
export function StationFirmware({ status, catalog, busy, online, superadmin, change }: {
  status?: StationUpdate; catalog?: StationFirmwareCatalog; busy: boolean; online: boolean; superadmin: boolean;
  change: (action: () => Promise<unknown>, success: string) => Promise<unknown>;
}) {
  if (!status) return null;
  const working = status.job && ['REQUESTED', 'DOWNLOADING', 'REBOOTING'].includes(status.job.state);
  return <div className="station-firmware" aria-label="Cập nhật phần mềm trạm">
    {working ? <>
      <b role="status">{stationUpdateLabel(status.job!)}</b>
      <p>Giữ nguồn điện cho trạm. Bạn có thể đóng trang này; trạm tự tiếp tục cập nhật.</p>
      {Date.now() - Date.parse(status.job!.updated_at) > 120000 && <p>Chưa nhận được tiến độ mới. Kiểm tra nguồn và Wi-Fi của trạm; khi có mạng trạm sẽ tự thử lại.</p>}
      {status.job!.state === 'REQUESTED' && <button className="secondary" disabled={busy} onClick={() => void change(() => post(`/printing/gateways/${status.id}/firmware/cancel`, {}), 'Đã hủy yêu cầu cập nhật.')}>Hủy cập nhật đang chờ</button>}
    </> : <>
      {status.job?.state === 'SUCCEEDED' && <p role="status">{stationUpdateLabel(status.job)}</p>}
      {status.available && catalog?.release ? <>
        <b>Có phiên bản mới · {catalog.release.version}</b>
        {status.capable ? <><p>Trạm sẽ tự tải và cài qua Wi-Fi. Việc in tạm dừng trong lúc cập nhật.</p>
          <button className="primary" disabled={busy || !online} onClick={() => {
            if (!confirm(`Cập nhật Trạm kết nối lên ${catalog.release!.version}? Giữ nguồn điện; việc in tạm dừng đến khi cập nhật xong.`)) return;
            void change(() => post(`/printing/gateways/${status.id}/firmware`, { releaseId: catalog.release!.id }, { feedback: { pending: 'Đang gửi yêu cầu cập nhật…', success: 'Đã gửi yêu cầu. Trạm sẽ tự tải và cài phần mềm.' } }), 'Đã gửi yêu cầu cập nhật. Theo dõi trạng thái tại thẻ trạm này.');
          }}>Cập nhật phần mềm</button>{!online && <p>Kết nối Wi-Fi cho trạm trước khi cập nhật.</p>}</>
          : <p>Trạm cần nâng cấp qua cáp USB một lần để bật cập nhật qua Wi-Fi. {superadmin && <Link to="/admin/esp32">Nâng cấp qua USB</Link>}</p>}
      </> : status.capable && <small>Phần mềm đã là phiên bản mới nhất.</small>}
    </>}
    <details><summary>Đổi Wi-Fi khi di chuyển trạm</summary><p>Khi không vào được mạng cũ khoảng 30 giây, trạm mở Wi-Fi PH-SETUP-… không có mật khẩu. Kết nối điện thoại vào đó, chọn mạng mới và nhập mật khẩu. Nếu trang không tự mở, vào http://192.168.4.1. Sau khi lưu thành công, cho điện thoại trở về mạng bình thường.</p></details>
  </div>;
}
